import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { Pencil, Plus, Trash2 } from 'lucide-react'

import type { TeaType } from '@/lib/api/tea-types-api'
import { PageBackButton } from '@/components/common/PageBackButton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import {
    useCreateTeaType,
    useDeleteTeaType,
    useTeaTypes,
    useUpdateTeaType,
} from '@/hooks/use-tea-types-query'
import { m } from '@/lib/paraglide/messages'
import { createTeaTypeSchema } from '@/lib/schemas/tea-type-schemas'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/agriculture/tea-types',
)({
    component: TeaTypesPage,
})

type FormValues = {
    name: string
    is_active: boolean
    is_default: boolean
}

function TeaTypesPage() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('tea_types.write')
    const [editing, setEditing] = useState<TeaType | null>(null)
    const [open, setOpen] = useState(false)

    const { data } = useTeaTypes({ per_page: 100 })
    const rows = data?.data ?? []

    const createMut = useCreateTeaType()
    const updateMut = useUpdateTeaType(editing?.id ?? '')
    const deleteMut = useDeleteTeaType()

    return (
        <div className="space-y-4 page-pad">
            <div className="flex items-center justify-between">
                <div className="flex min-w-0 items-center gap-2">
                    <PageBackButton className="-my-2 md:-my-1" />
                    <h1 className="text-xl font-semibold">
                        {m.tea_type_title()}
                    </h1>
                </div>
                {canWrite && (
                    <Button
                        size="sm"
                        onClick={() => {
                            setEditing(null)
                            setOpen(true)
                        }}
                    >
                        <Plus className="size-4" /> {m.tea_type_new()}
                    </Button>
                )}
            </div>

            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>{m.tea_type_code()}</TableHead>
                        <TableHead>{m.tea_type_name()}</TableHead>
                        <TableHead>{m.tea_type_active()}</TableHead>
                        <TableHead>{m.tt_is_default()}</TableHead>
                        <TableHead />
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={row.id}>
                            <TableCell className="font-mono text-sm">
                                {row.code}
                            </TableCell>
                            <TableCell>{row.name}</TableCell>
                            <TableCell>
                                <Badge
                                    variant={
                                        row.is_active ? 'default' : 'secondary'
                                    }
                                >
                                    {row.is_active ? '✓' : '—'}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                {row.is_default ? (
                                    <Badge>{m.tt_default_badge()}</Badge>
                                ) : (
                                    <span className="text-muted-foreground">
                                        —
                                    </span>
                                )}
                            </TableCell>
                            <TableCell className="text-right space-x-1">
                                {canWrite && (
                                    <>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => {
                                                setEditing(row)
                                                setOpen(true)
                                            }}
                                        >
                                            <Pencil className="size-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => {
                                                if (
                                                    window.confirm(
                                                        m.tea_type_delete_confirm(),
                                                    )
                                                ) {
                                                    deleteMut.mutate(row.id)
                                                }
                                            }}
                                        >
                                            <Trash2 className="size-4" />
                                        </Button>
                                    </>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {editing ? m.tea_type_edit() : m.tea_type_new()}
                        </DialogTitle>
                    </DialogHeader>
                    <TeaTypeForm
                        initial={editing}
                        onSubmit={async (values) => {
                            try {
                                if (editing) {
                                    await updateMut.mutateAsync(values)
                                } else {
                                    await createMut.mutateAsync(values)
                                }
                                setOpen(false)
                            } catch {
                                // onError handler in the mutation hook surfaces the toast;
                                // dialog stays open so the user can correct the input.
                            }
                        }}
                        submitting={createMut.isPending || updateMut.isPending}
                    />
                </DialogContent>
            </Dialog>
        </div>
    )
}

function TeaTypeForm({
    initial,
    onSubmit,
    submitting,
}: {
    initial: TeaType | null
    onSubmit: (v: FormValues) => Promise<void>
    submitting: boolean
}) {
    const schema = createTeaTypeSchema()
    const form = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: {
            name: initial?.name ?? '',
            is_active: initial?.is_active ?? true,
            is_default: initial?.is_default ?? false,
        },
    })
    const isActive = form.watch('is_active')
    return (
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
            {/* Mã loại chè do hệ thống tự tạo (TTNN); không hiển thị/nhập trong
                form — xem mã ở cột "Mã loại chè" trên danh sách. */}
            <div>
                <Label htmlFor="tea-type-name">{m.tea_type_name()}</Label>
                <Input id="tea-type-name" {...form.register('name')} />
                {form.formState.errors.name && (
                    <p className="text-xs text-red-500">
                        {form.formState.errors.name.message}
                    </p>
                )}
            </div>
            <div className="flex items-center gap-2">
                <Switch
                    id="tea-type-active"
                    checked={isActive}
                    onCheckedChange={(v) => {
                        form.setValue('is_active', v)
                        // Giống đã tắt không được là mặc định (backend cũng bỏ cờ).
                        if (!v) form.setValue('is_default', false)
                    }}
                />
                <Label htmlFor="tea-type-active">{m.tea_type_active()}</Label>
            </div>
            <div className="space-y-1">
                <div className="flex items-center gap-2">
                    <Switch
                        id="tea-type-default"
                        aria-describedby="tea-type-default-hint"
                        checked={form.watch('is_default')}
                        disabled={!isActive}
                        onCheckedChange={(v) => form.setValue('is_default', v)}
                    />
                    <Label htmlFor="tea-type-default">
                        {m.tt_is_default()}
                    </Label>
                </div>
                <p
                    id="tea-type-default-hint"
                    className="text-xs text-muted-foreground"
                >
                    {m.tt_is_default_hint()}
                </p>
            </div>
            <DialogFooter>
                <Button type="submit" disabled={submitting}>
                    {submitting ? '...' : m.tea_type_save()}
                </Button>
            </DialogFooter>
        </form>
    )
}
